import { useEffect, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  getSegmentAnalysisConfig,
  type SegmentAnalysisAddressableLine,
  type SegmentAnalysisConfig,
  updateSegmentAnalysisConfig,
} from "@/api/admin-api"
import { ArrowDown, ArrowUp, BarChart3, Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { COMPANY_NAME } from "@/features/segment-analysis/utils/branding"

import { AdminSettingsLoadState } from "./admin-settings-load-state"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { adminQueryKeys } from "../hooks/admin-query-keys"

function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string
  label: string
  description?: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <Label htmlFor={id}>{label}</Label>
        {description ? <p className="text-muted-foreground text-xs">{description}</p> : null}
      </div>
      <Switch checked={checked} id={id} onCheckedChange={onChange} />
    </div>
  )
}

/**
 * Editor for the tenant's addressable-opportunity lines (§G8). Drives the
 * portfolio KPIs/cards/hero — Nolato defaults to Plastics + Box Build, but any
 * tenant renames/reorders/adds lines here (key is slug-safe; the backend merge
 * re-validates + caps the list). An empty list degrades to a single generic
 * "Addressable market".
 */
function AddressableLinesEditor({
  lines,
  onChange,
}: {
  lines: SegmentAnalysisAddressableLine[]
  onChange: (next: SegmentAnalysisAddressableLine[]) => void
}) {
  const { t } = useTranslation()
  const update = (index: number, values: Partial<SegmentAnalysisAddressableLine>) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, ...values } : line)))
  const remove = (index: number) => onChange(lines.filter((_, i) => i !== index))
  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= lines.length) return
    const next = [...lines]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {lines.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.segmentAnalysis.addressableEmpty",
            "No addressable lines — the portfolio shows a single generic “Addressable market”."
          )}
        </p>
      ) : null}
      {lines.map((line, index) => (
        <div
          key={index}
          className="border-border grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_auto]"
        >
          <div className="space-y-1">
            <Label htmlFor={`sa-addr-label-${index}`}>
              {t("adminDashboard.segmentAnalysis.addressableLabel", "Label")}
            </Label>
            <Input
              id={`sa-addr-label-${index}`}
              placeholder="Addressable Plastics"
              value={line.label}
              onChange={(e) => update(index, { label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`sa-addr-key-${index}`}>
              {t("adminDashboard.segmentAnalysis.addressableKey", "Key")}
            </Label>
            <Input
              id={`sa-addr-key-${index}`}
              placeholder="plastics"
              value={line.key}
              onChange={(e) => update(index, { key: e.target.value })}
            />
          </div>
          <div className="flex items-end gap-1">
            <Button
              aria-label={t("common.moveUp", "Move up")}
              disabled={index === 0}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => move(index, -1)}
            >
              <ArrowUp className="size-4" />
            </Button>
            <Button
              aria-label={t("common.moveDown", "Move down")}
              disabled={index === lines.length - 1}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => move(index, 1)}
            >
              <ArrowDown className="size-4" />
            </Button>
            <Button
              aria-label={t("common.remove", "Remove")}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => remove(index)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          <div className="space-y-1 sm:col-span-3">
            <Label htmlFor={`sa-addr-sublabel-${index}`}>
              {t("adminDashboard.segmentAnalysis.addressableSublabel", "Sub-label (optional)")}
            </Label>
            <Input
              id={`sa-addr-sublabel-${index}`}
              placeholder={`${COMPANY_NAME} assumed`}
              value={line.sublabel ?? ""}
              onChange={(e) => update(index, { sublabel: e.target.value })}
            />
          </div>
        </div>
      ))}
      <Button
        size="sm"
        type="button"
        variant="outline"
        onClick={() => onChange([...lines, { key: "", label: "" }])}
      >
        <Plus className="mr-1 size-4" />
        {t("adminDashboard.segmentAnalysis.addAddressableLine", "Add addressable line")}
      </Button>
    </div>
  )
}

export function SegmentAnalysisConfigPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SegmentAnalysisConfig | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const {
    data: config,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: adminQueryKeys.segmentAnalysisConfig(),
    queryFn: getSegmentAnalysisConfig,
    staleTime: 60_000,
  })

  // Seed local draft from server config once it loads (and on refetch when there
  // are no unsaved local edits).
  useEffect(() => {
    if (config && !hasChanges) setDraft(config)
  }, [config, hasChanges])

  const saveMutation = useMutation({
    mutationFn: updateSegmentAnalysisConfig,
    onSuccess: () => {
      toast.success(t("adminDashboard.segmentAnalysis.saved", "Segment Analysis settings saved"))
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.segmentAnalysisConfig() })
      setHasChanges(false)
    },
    onError: () => {
      toast.error(t("adminDashboard.segmentAnalysis.saveError", "Failed to save settings"))
    },
  })

  function patch(section: keyof SegmentAnalysisConfig, values: Record<string, unknown>) {
    setDraft((prev) => {
      const base = prev ?? config
      if (!base) return prev
      return { ...base, [section]: { ...base[section], ...values } }
    })
    setHasChanges(true)
  }

  return (
    <CollapsibleManagementSection
      headerActions={<DelegationModeControl capability="segment_analysis" />}
      icon={<BarChart3 className="size-5" />}
      id="segment-analysis"
      isDirty={hasChanges}
      storageKey="admin-management-segment-analysis-expanded"
      title={t("adminDashboard.segmentAnalysis.title", "Segment Analysis")}
    >
      {isLoading || !draft ? (
        <AdminSettingsLoadState
          loading={isLoading || !isError}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="space-y-8">
          {/* Cost protection */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold">
              {t("adminDashboard.segmentAnalysis.costProtection", "Cost protection")}
            </h4>
            <ToggleRow
              checked={draft.costProtection.enabled}
              id="sa-cost-enabled"
              label={t("adminDashboard.segmentAnalysis.costEnabled", "Enable cost protection")}
              description={t(
                "adminDashboard.segmentAnalysis.costEnabledDesc",
                "Track monthly LLM spend and enforce a ceiling."
              )}
              onChange={(v) => patch("costProtection", { enabled: v })}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sa-ceiling">
                  {t("adminDashboard.segmentAnalysis.monthlyCeiling", "Monthly ceiling")}
                </Label>
                <Input
                  id="sa-ceiling"
                  min={0}
                  type="number"
                  value={draft.costProtection.monthlyCeiling}
                  onChange={(e) =>
                    patch("costProtection", { monthlyCeiling: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sa-currency">
                  {t("adminDashboard.segmentAnalysis.currency", "Currency")}
                </Label>
                <Select
                  value={draft.costProtection.currency}
                  onValueChange={(v) => patch("costProtection", { currency: v })}
                >
                  <SelectTrigger id="sa-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEK">SEK</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sa-token-cap">
                  {t("adminDashboard.segmentAnalysis.perRunTokenCap", "Per-run token cap")}
                </Label>
                <Input
                  id="sa-token-cap"
                  min={1000}
                  type="number"
                  value={draft.costProtection.perRunTokenCap}
                  onChange={(e) =>
                    patch("costProtection", { perRunTokenCap: Number(e.target.value) })
                  }
                />
              </div>
            </div>
            <ToggleRow
              checked={draft.costProtection.blockManualOnCeiling}
              id="sa-block-manual"
              description={t(
                "adminDashboard.segmentAnalysis.blockManualDesc",
                "Block manual runs once the ceiling is reached (otherwise warn and allow)."
              )}
              label={t(
                "adminDashboard.segmentAnalysis.blockManual",
                "Hard-stop manual runs at ceiling"
              )}
              onChange={(v) => patch("costProtection", { blockManualOnCeiling: v })}
            />
          </section>

          {/* Scheduling */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold">
              {t("adminDashboard.segmentAnalysis.scheduling", "Scheduled refresh")}
            </h4>
            <ToggleRow
              checked={draft.scheduling.enabled}
              id="sa-sched-enabled"
              description={t(
                "adminDashboard.segmentAnalysis.schedulingDesc",
                "Off by default. When on, cheap delta runs only — deep runs stay manual."
              )}
              label={t(
                "adminDashboard.segmentAnalysis.schedulingEnabled",
                "Enable scheduled refresh"
              )}
              onChange={(v) => patch("scheduling", { enabled: v })}
            />
          </section>

          {/* Auto-approval */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold">
              {t("adminDashboard.segmentAnalysis.autoApproval", "Auto-approval")}
            </h4>
            <ToggleRow
              checked={draft.autoApproval.enabled}
              id="sa-auto-enabled"
              description={t(
                "adminDashboard.segmentAnalysis.autoApprovalDesc",
                "Off by default. When on, only trivial deltas auto-approve (audited as 'auto')."
              )}
              label={t(
                "adminDashboard.segmentAnalysis.autoApprovalEnabled",
                "Auto-approve trivial changes"
              )}
              onChange={(v) => patch("autoApproval", { enabled: v })}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sa-tolerance">
                  {t("adminDashboard.segmentAnalysis.numericTolerance", "Numeric tolerance (%)")}
                </Label>
                <Input
                  id="sa-tolerance"
                  min={0}
                  type="number"
                  value={draft.autoApproval.numericTolerancePct}
                  onChange={(e) =>
                    patch("autoApproval", { numericTolerancePct: Number(e.target.value) })
                  }
                />
              </div>
            </div>
            <ToggleRow
              checked={draft.autoApproval.allowDateOnly}
              id="sa-allow-date"
              label={t("adminDashboard.segmentAnalysis.allowDateOnly", "Allow date-only changes")}
              onChange={(v) => patch("autoApproval", { allowDateOnly: v })}
            />
            <ToggleRow
              checked={draft.autoApproval.allowSourceListOnly}
              id="sa-allow-sources"
              label={t(
                "adminDashboard.segmentAnalysis.allowSourceListOnly",
                "Allow source-list changes"
              )}
              onChange={(v) => patch("autoApproval", { allowSourceListOnly: v })}
            />
          </section>

          {/* Notifications */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold">
              {t("adminDashboard.segmentAnalysis.notifications", "Notifications")}
            </h4>
            <ToggleRow
              checked={draft.notifications.inApp}
              id="sa-notif-inapp"
              label={t("adminDashboard.segmentAnalysis.inApp", "In-app (always recommended)")}
              onChange={(v) => patch("notifications", { inApp: v })}
            />
            <ToggleRow
              checked={draft.notifications.email}
              id="sa-notif-email"
              label={t("adminDashboard.segmentAnalysis.email", "Email")}
              description={t(
                "adminDashboard.segmentAnalysis.emailDesc",
                "Requires the user-facing sender app (USER_FACING_EMAIL_* env)."
              )}
              onChange={(v) => patch("notifications", { email: v })}
            />
            <div className="space-y-1">
              <Label htmlFor="sa-email-recipients">
                {t(
                  "adminDashboard.segmentAnalysis.emailRecipients",
                  "Email recipients (one per line)"
                )}
              </Label>
              <Textarea
                id="sa-email-recipients"
                rows={3}
                value={draft.notifications.emailRecipients.join("\n")}
                onChange={(e) =>
                  patch("notifications", {
                    emailRecipients: e.target.value
                      .split(/[\n,]/)
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
            <ToggleRow
              checked={draft.notifications.teams}
              id="sa-notif-teams"
              label={t("adminDashboard.segmentAnalysis.teams", "Teams")}
              description={t(
                "adminDashboard.segmentAnalysis.teamsDesc",
                "Requires USER_FACING_TEAMS_WEBHOOK_URL env."
              )}
              onChange={(v) => patch("notifications", { teams: v })}
            />
          </section>

          {/* Presentation (§G8) — tenant-configurable addressable lines */}
          <section className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold">
                {t("adminDashboard.segmentAnalysis.presentation", "Presentation")}
              </h4>
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.segmentAnalysis.addressableDesc",
                  "Addressable-opportunity lines shown in the portfolio KPIs, cards and segment header."
                )}
              </p>
            </div>
            <AddressableLinesEditor
              lines={draft.presentation.addressableLines}
              onChange={(addressableLines) => patch("presentation", { addressableLines })}
            />
          </section>

          {hasChanges && (
            <Button
              disabled={saveMutation.isPending}
              size="sm"
              onClick={() => saveMutation.mutate(draft)}
            >
              {saveMutation.isPending
                ? t("common.saving", "Saving...")
                : t("adminDashboard.segmentAnalysis.save", "Save Changes")}
            </Button>
          )}
        </div>
      )}
    </CollapsibleManagementSection>
  )
}
